/*
 * Two-handle price slider for the native price_range facet.
 * Markup: snippets/price-range-slider.liquid   Styles: assets/component-facets.css
 *
 * The whole design rests on one thing: this component never talks to the network and never
 * builds a filter URL. The two number inputs the theme already renders are the source of
 * truth. This writes into them and fires `input`, which the facet form is already listening
 * for on a 500ms debounce (facets.js:13). So a drag ends in exactly one request, the param
 * names can change in a future theme update without touching this file, and turning the
 * feature off is deleting one script tag.
 *
 * It also survives a facet re-render for free. facets.js:112 skips re-rendering the .js-filter
 * that contains event.target, and event.target here is one of those inputs, so the price filter
 * is the one block left alone while the shopper is using it. When any OTHER filter changes this
 * block IS replaced, the element is constructed again, and connectedCallback reads the new
 * server bounds. Element lifecycle is the only reset signal available; nothing is dispatched.
 */
(function () {
  'use strict';

  // facets.liquid renders a price filter twice, once for the mobile drawer and once for
  // desktop, and each render carries this script tag. Defining twice throws.
  if (window.customElements && customElements.get('price-range-slider')) return;

  var clamp = function (v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
  };

  class PriceRangeSlider extends HTMLElement {
    connectedCallback() {
      if (this.booted) return;

      // The number inputs are the only thing worth driving. Rendered as our next sibling;
      // the parentElement lookup is the fallback for a future markup shuffle.
      var host =
        this.nextElementSibling && this.nextElementSibling.matches('price-range')
          ? this.nextElementSibling
          : this.parentElement && this.parentElement.querySelector('price-range');
      var inputs = host ? host.querySelectorAll('input') : [];
      if (inputs.length < 2) return;

      this.max = Number(this.dataset.max);
      // A collection where every product costs the same, or a filtered set with no prices at
      // all, has nothing to drag. Stay hidden and leave the two fields to do their job.
      if (!isFinite(this.max) || this.max <= 0) return;

      this.booted = true;
      this.minInput = inputs[0];
      this.maxInput = inputs[1];

      this.track = this.querySelector('.price-slider__track');
      this.handleMin = this.querySelector('[data-handle="min"]');
      this.handleMax = this.querySelector('[data-handle="max"]');
      this.bars = this.querySelector('.price-slider__bars');
      this.outMin = this.querySelector('.price-slider__out--min');
      this.outMax = this.querySelector('.price-slider__out--max');
      if (!this.track || !this.handleMin || !this.handleMax) return;

      this.min = 0;
      this.currency = this.dataset.currency || '';
      this.locale = document.documentElement.lang || undefined;

      // Round numbers to drag through. A 12,000 shekel range stepping by 1 makes the handle
      // feel like it is fighting the finger; a 200 shekel range stepping by 50 cannot express
      // most of it.
      this.step = this.max > 4000 ? 50 : this.max > 1500 ? 20 : this.max > 400 ? 10 : this.max > 100 ? 5 : 1;

      var rawMin = this.dataset.valueMin;
      var rawMax = this.dataset.valueMax;
      this.lo = rawMin === undefined ? this.min : clamp(Number(rawMin) || this.min, this.min, this.max);
      this.hi = rawMax === undefined ? this.max : clamp(Number(rawMax) || this.min, this.min, this.max);
      // Only a genuinely INVERTED pair is nonsense. An equal pair is a real filter a shopper
      // produces by typing the same number in both fields, or by opening a shared URL, and
      // rejecting it painted the full range over a filter the grid was really applying.
      if (!(this.hi >= this.lo)) {
        this.lo = this.min;
        this.hi = this.max;
      }

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      // A shopper can still type into the two fields, and facets.js deliberately does NOT
      // re-render the filter block they are interacting with, so nothing else would ever tell
      // the handles about it. Without this the slider went on painting the full range over a
      // real filter, and the next drag committed its own stale bounds over the typed value.
      this.onFieldChange = this.onFieldChange.bind(this);
      this.minInput.addEventListener('change', this.onFieldChange);
      this.maxInput.addEventListener('change', this.onFieldChange);

      [this.handleMin, this.handleMax].forEach((handle) => {
        handle.addEventListener('pointerdown', this.onPointerDown);
        handle.addEventListener('pointermove', this.onPointerMove);
        handle.addEventListener('pointerup', this.onPointerUp);
        handle.addEventListener('pointercancel', this.onPointerUp);
        handle.addEventListener('keydown', this.onKeyDown);
      });

      this.buildBars();
      this.paint();
      this.hidden = false;
    }

    // Draws the precomputed distribution as one span per bucket, positioned by PRICE against
    // the same maximum the handles use. Buckets are uniform in price, so they would be uniform
    // in pixels too if the chart and the track always spanned the same range, but they do not:
    // an active brand filter shrinks range_max while the stored distribution keeps the whole
    // collection's spread. Positioning each bar by its own price boundaries is what keeps the
    // chart honest under that, and lets a bucket that runs off the end simply be dropped.
    buildBars() {
      var raw = this.dataset.histogram;
      if (!raw || !this.bars) return;

      var data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        return;
      }
      var width = Number(data && data.w);
      var counts = data && data.c;
      if (!(width > 0) || !Array.isArray(counts) || !counts.length) return;

      // Scale to the tallest bucket that is actually on screen, not to the tallest overall.
      // Otherwise a filtered collection whose big bucket has been cropped away renders every
      // remaining bar as a barely visible sliver.
      var visible = [];
      for (var i = 0; i < counts.length; i++) {
        var from = i * width;
        if (from >= this.max) break;
        visible.push({ from: from, to: Math.min((i + 1) * width, this.max), count: Number(counts[i]) || 0 });
      }
      if (!visible.length) return;

      var tallest = visible.reduce(function (m, b) {
        return Math.max(m, b.count);
      }, 0);
      if (!tallest) return;

      var span = this.max - this.min || 1;
      var frag = document.createDocumentFragment();
      visible.forEach((b) => {
        var bar = document.createElement('span');
        bar.className = 'price-slider__bar';
        bar.dataset.from = String(b.from);
        bar.dataset.to = String(b.to);
        bar.style.setProperty('--ws-bar-start', ((b.from - this.min) / span) * 100 + '%');
        bar.style.setProperty('--ws-bar-width', ((b.to - b.from) / span) * 100 + '%');
        // A floor, so a bucket holding one product still reads as "a few" rather than nothing.
        bar.style.setProperty('--ws-bar-height', (b.count ? Math.max(8, (b.count / tallest) * 100) : 0) + '%');
        // A real child rather than a ::after. The pseudo-element version looked right and was
        // not: a percentage inline-size on it resolved to the full bar width whatever the
        // variable said, and there is no way to measure a pseudo-element's box to catch that.
        // One span per bucket is 28 nodes on a page that already has thousands, and it can be
        // proven correct.
        var fill = document.createElement('span');
        fill.className = 'price-slider__bar-in';
        bar.appendChild(fill);
        frag.appendChild(bar);
      });
      this.bars.appendChild(frag);
      this.barEls = Array.prototype.slice.call(this.bars.children);
      this.classList.add('price-slider--has-bars');
    }

    get rtl() {
      return getComputedStyle(this).direction === 'rtl';
    }

    onPointerDown(event) {
      // Ignore anything but the primary button, so a right-click cannot start a drag that
      // never gets a pointerup.
      if (event.button && event.button !== 0) return;
      // One drag at a time. this.dragging is a single slot, so a second finger landing on the
      // other handle while the first is still down used to repoint the first finger's moves at
      // the other handle, and the range jumped.
      if (this.active) return;
      this.rect = this.track.getBoundingClientRect();
      this.active = true;
      this.pointerId = event.pointerId;
      // Stacked on the same value, the press cannot say which handle is meant. Leave it
      // unresolved and let the first movement decide; see onPointerMove.
      this.dragging =
        this.lo === this.hi ? null : this.pickHandle(event.clientX, event.currentTarget.dataset.handle);
      // Every subsequent move and the release are delivered to the element that was actually
      // pressed even when the finger leaves it, which is what makes a fast drag off the end of
      // the track behave. It also means there is nothing bound at document level to leak on
      // re-render. The listeners live on that element, so routing stays right even when the
      // handle being driven is the other one.
      if (event.currentTarget.setPointerCapture) {
        try {
          event.currentTarget.setPointerCapture(event.pointerId);
        } catch (e) {}
      }
      this.classList.add('price-slider--dragging');
      if (this.dragging) (this.dragging === 'min' ? this.handleMin : this.handleMax).focus();
      event.preventDefault();
    }

    // Which handle a press should drive when the two are apart. Chosen by proximity rather
    // than by which element the browser hit, so a press that lands on the overlap of two
    // nearly-touching handles goes to the nearer one instead of to whichever paints last.
    pickHandle(clientX, hit) {
      var value = this.valueAt(clientX);
      if (value === null) return hit;
      return Math.abs(value - this.lo) <= Math.abs(value - this.hi) ? 'min' : 'max';
    }

    onPointerMove(event) {
      if (!this.active || event.pointerId !== this.pointerId) return;
      event.preventDefault();

      if (!this.dragging) {
        // The handles were stacked when the press landed. Whichever way the shopper moves, the
        // handle that can actually get there is the one they mean: stacked at the top only the
        // minimum can come down, stacked at the bottom only the maximum can go up, and stacked
        // in the middle either can move. Resolving here instead of at press time is what stops
        // a shopper who shoves one handle onto the other from being pinned there for good.
        var reached = this.valueAt(event.clientX);
        if (reached === null || reached === this.lo) return;
        this.dragging = reached < this.lo ? 'min' : 'max';
        (this.dragging === 'min' ? this.handleMin : this.handleMax).focus();
      }

      this.setFromClientX(event.clientX);
    }

    onPointerUp(event) {
      if (!this.active || (event && event.pointerId !== this.pointerId)) return;
      this.active = false;
      this.pointerId = null;
      this.dragging = null;
      this.rect = null;
      this.classList.remove('price-slider--dragging');
      this.commit();
    }

    // The price under a pointer, snapped to the step. Returns null when there is nothing to
    // measure against, which is what a closed details drawer looks like.
    valueAt(clientX) {
      var rect = this.rect || this.track.getBoundingClientRect();
      if (!rect.width) return null;

      // The only place the writing direction matters. Position and fill are expressed with
      // logical properties so CSS mirrors itself; a pointer coordinate cannot mirror itself.
      var ratio = this.rtl ? (rect.right - clientX) / rect.width : (clientX - rect.left) / rect.width;
      ratio = clamp(ratio, 0, 1);

      // Snapping to `step` would quietly stop short of a bound that is not a multiple of it:
      // dragged fully right on a 1,234.90 range with a step of 20 lands on 1,220 and hides the
      // most expensive product in the collection. At the extremes take the bound exactly.
      if (ratio >= 0.999) return this.max;
      if (ratio <= 0.001) return this.min;
      return clamp(Math.round((this.min + ratio * (this.max - this.min)) / this.step) * this.step, this.min, this.max);
    }

    setFromClientX(clientX) {
      var value = this.valueAt(clientX);
      if (value === null) return;

      if (this.dragging === 'min') this.lo = Math.min(value, this.hi);
      else this.hi = Math.max(value, this.lo);

      this.paint();
    }

    onKeyDown(event) {
      var which = event.currentTarget.dataset.handle;
      var key = event.key;
      var dir = 0;

      // Arrows follow what the shopper sees, so they invert in RTL. Up and down do not:
      // vertically, more is always up.
      if (key === 'ArrowLeft') dir = this.rtl ? 1 : -1;
      else if (key === 'ArrowRight') dir = this.rtl ? -1 : 1;
      else if (key === 'ArrowUp') dir = 1;
      else if (key === 'ArrowDown') dir = -1;
      else if (key === 'Home' || key === 'End') {
        var toStart = key === 'Home';
        if (which === 'min') this.lo = toStart ? this.min : this.hi;
        else this.hi = toStart ? this.lo : this.max;
        event.preventDefault();
        this.paint();
        this.commit();
        return;
      }
      if (!dir) return;
      event.preventDefault();

      var amount = (event.shiftKey ? this.step * 10 : this.step) * dir;
      if (which === 'min') this.lo = clamp(this.lo + amount, this.min, this.hi);
      else this.hi = clamp(this.hi + amount, this.lo, this.max);

      this.paint();
      // Safe to fire on every keypress: the form's own 500ms debounce collapses a held arrow
      // into one request after the shopper stops.
      this.commit();
    }

    format(value) {
      return this.currency + value.toLocaleString(this.locale, { maximumFractionDigits: 0 });
    }

    paint() {
      var span = this.max - this.min || 1;
      var a = ((this.lo - this.min) / span) * 100;
      var b = ((this.hi - this.min) / span) * 100;

      // Two custom properties instead of six style writes. CSS derives the handle offsets and
      // the fill inline-size from them with calc(), and being logical properties they mirror
      // for RTL without a second rule.
      this.style.setProperty('--ws-price-lo', a + '%');
      this.style.setProperty('--ws-price-hi', b + '%');

      // How much of each bucket the shopper has chosen, as a fraction of that bucket.
      //
      // Lighting whole buckets was the obvious version and it was subtly wrong: the handle steps
      // in 10s while a bucket spans 50, so stopping at 350 left a 300-400 bar either fully lit or
      // fully dark, and the chart disagreed with the number above it by up to half a bucket. The
      // fraction is exact at any handle position, and it costs nothing: CSS paints it with a
      // pseudo-element, so there is no extra node per bar.
      if (this.barEls) {
        for (var i = 0; i < this.barEls.length; i++) {
          var bar = this.barEls[i];
          var from = Number(bar.dataset.from);
          var to = Number(bar.dataset.to);
          var width = to - from;
          var covered = Math.min(to, this.hi) - Math.max(from, this.lo);
          var frac = width > 0 ? Math.max(0, Math.min(1, covered / width)) : 0;
          var fill = bar.firstElementChild;
          if (fill) fill.style.inlineSize = frac * 100 + '%';
        }
      }

      if (this.outMin) this.outMin.textContent = this.format(this.lo);
      if (this.outMax) this.outMax.textContent = this.format(this.hi);

      this.handleMin.setAttribute('aria-valuenow', String(this.lo));
      this.handleMax.setAttribute('aria-valuenow', String(this.hi));
      // Each handle is clamped by the OTHER one, and Home/End travel to it rather than to the
      // collection's range. Announcing the static range would tell a screen reader the keys can
      // reach somewhere they cannot.
      this.handleMin.setAttribute('aria-valuemax', String(this.hi));
      this.handleMax.setAttribute('aria-valuemin', String(this.lo));
      // Screen readers should say the price, not the bare number.
      this.handleMin.setAttribute('aria-valuetext', this.format(this.lo));
      this.handleMax.setAttribute('aria-valuetext', this.format(this.hi));
    }

    onFieldChange() {
      // The theme's PriceRange listens for change on these fields too and may clamp what was
      // typed. It binds after this element, since it sits later in the DOM, so read on the next
      // turn of the loop to pick up the value that actually survived rather than the keystroke.
      Promise.resolve().then(() => {
        if (!this.isConnected) return;
        var lo = this.minInput.value === '' ? this.min : Number(this.minInput.value);
        var hi = this.maxInput.value === '' ? this.max : Number(this.maxInput.value);
        if (!isFinite(lo)) lo = this.min;
        if (!isFinite(hi)) hi = this.max;
        this.lo = clamp(lo, this.min, this.max);
        this.hi = clamp(hi, this.min, this.max);
        if (this.hi < this.lo) this.hi = this.lo;
        this.paint();
      });
    }

    commit() {
      // Blank rather than the bound itself. Writing 0 and range_max would put a filter in the
      // URL and an active-filter chip in the header for a range that excludes nothing, and the
      // shopper would get a Clear button that appears to do nothing.
      var lo = this.lo <= this.min ? '' : String(this.lo);
      var hi = this.hi >= this.max ? '' : String(this.hi);
      if (this.minInput.value === lo && this.maxInput.value === hi) return;

      this.minInput.value = lo;
      this.maxInput.value = hi;

      // Keep the two fields' own clamps consistent, which is what stops a shopper who types
      // afterwards from setting a floor above the ceiling.
      //
      // Done here rather than by dispatching `change` at the theme's PriceRange element and
      // letting it do the same job. That element runs adjustToValidValues on the event target,
      // and it reads an EMPTY field as the number 0: handed the max field we had just blanked,
      // it saw 0 below the floor we had just installed and rewrote the field to the floor. A
      // shopper dragging the minimum to 400 got filter.v.price.lte=400 alongside gte=400, so
      // "₪400 and up" came back as "exactly ₪400" and the grid emptied. Reproduced against the
      // real class before this was written; never hand it a blank.
      this.maxInput.setAttribute('min', lo === '' ? '0' : lo);
      this.minInput.setAttribute('max', hi === '' ? String(this.max) : hi);

      // `input` is the one the form is listening for (facets.js:13). One dispatch is enough:
      // the handler reads the whole form with FormData rather than the event target value.
      // PriceRange listens only for `change`, so this cannot re-enter the clamping path.
      this.minInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  customElements.define('price-range-slider', PriceRangeSlider);
})();
